import React, { useEffect, useState, useRef } from 'react';
import './PropertySelection.scss';
import HTLogo from '../assets/ht_orange_logo_name.svg';
import LeftBg from '../assets/leftside_bg_grey.svg';
import RightBg from '../assets/rightside_bg_grey.svg';
import { Formik, Form, FormikProps } from 'formik';
import Modal from '../shared/molecules/modal/Modal';
import { PropertyCheckList, PropertyDetailsModal, AuthorizeEmailModal } from 'shared';
import type { PropertyDetailsData, PropertyForDetails } from 'shared';
import CheckTick from '../assets/check_tick.svg';
import Loader from '../shared/atoms/loader/Loader';
import {
  useGetPropertiesQuery,
  useSubmitSelectedPropertiesMutation,
  useGetRatePlansQuery,
  type Property as PropertyType,
} from './apaleoSlice';

interface PropertyWithDetails {
  propertyId: string;
  propertyCode: string;
  propertyName: string;
  ratePlans: Array<{
    id: string;
    code: string;
    name: string;
    description: string;
  }>;
  details?: PropertyDetailsData;
}

const PropertySelection: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [propertiesWithRatePlans, setPropertiesWithRatePlans] = useState<
    PropertyType[]
  >([]);
  const [ratePlansFetched, setRatePlansFetched] = useState<
    Record<string, boolean>
  >({});

  // State for property details modal flow (Step 1: collect details for all properties)
  const [showPropertyDetailsModal, setShowPropertyDetailsModal] = useState(false);
  const [propertiesToCollectDetails, setPropertiesToCollectDetails] = useState<PropertyWithDetails[]>([]);
  const [collectedPropertyDetails, setCollectedPropertyDetails] = useState<Record<string, PropertyDetailsData>>({});

  // State for email authorization modal (Step 2: collect email once)
  const [showEmailModal, setShowEmailModal] = useState(false);

  const formikRef = useRef<FormikProps<{ selections: Record<string, string[]> }>>(null);

  const {
    data: properties = [],
    isLoading: isLoadingProperties,
    isError: isPropertiesError,
  } = useGetPropertiesQuery(undefined, { refetchOnMountOrArgChange: true });

  // eslint-disable-next-line
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [currentPropertyIdForRatePlans, setCurrentPropertyIdForRatePlans] =
    useState<string | null>(null);
  // eslint-disable-next-line
  const [submitSelectedProperties, { isLoading: isSubmitting }] =
    useSubmitSelectedPropertiesMutation();

  // eslint-disable-next-line
  const { data: ratePlansData, isFetching: isFetchingRatePlans } =
    useGetRatePlansQuery(
      { propertyId: currentPropertyIdForRatePlans! },
      {
        skip:
          !currentPropertyIdForRatePlans ||
          ratePlansFetched[currentPropertyIdForRatePlans],
      }
    );

  // Update properties with fetched rate plans
  useEffect(() => {
    if (
      ratePlansData?.success &&
      currentPropertyIdForRatePlans &&
      ratePlansData.ratePlans
    ) {
      setPropertiesWithRatePlans((prev) =>
        prev.map((prop) =>
          prop.id === currentPropertyIdForRatePlans
            ? {
                ...prop,
                ratePlans: ratePlansData.ratePlans.map((rp) => ({
                  id: rp.id,
                  name: rp.name,
                  code: rp.code,
                  description: rp.description || '',
                })),
              }
            : prop
        )
      );

      // Mark this property as fetched
      setRatePlansFetched((prev) => ({
        ...prev,
        [currentPropertyIdForRatePlans]: true,
      }));
      setCurrentPropertyIdForRatePlans(null);
    }
  }, [ratePlansData, currentPropertyIdForRatePlans]);

  // Initialize properties when properties are loaded
  useEffect(() => {
    if (properties?.properties?.length) {
      setPropertiesWithRatePlans([...properties.properties]);
    }
  }, [properties]);

  const handlePropertySelection = (selected: Record<string, string[]>) => {
    const propertyIds = Object.keys(selected);
    setSelectedPropertyIds(propertyIds);
  };

  const handlePropertyExpand = (propertyId: string) => {
    // Fetch rate plans when property is expanded (if not already fetched)
    if (
      !ratePlansFetched[propertyId] &&
      !propertiesWithRatePlans.find((p) => p.id === propertyId)?.ratePlans
    ) {
      setCurrentPropertyIdForRatePlans(propertyId);
    }
  };

  // Validation: Check if all selected properties have at least one rate plan
  const isValidSelection = (selections: Record<string, string[]>): boolean => {
    // If no properties selected, invalid
    if (Object.keys(selections).length === 0) {
      return false;
    }

    // Check if every selected property has at least one rate plan
    return Object.entries(selections).every(([_propertyId, ratePlanIds]) => {
      return Array.isArray(ratePlanIds) && ratePlanIds.length > 0;
    });
  };

  // Step 1: Handle property details submission from modal (all properties at once)
  const handlePropertyDetailsSubmit = (data: Record<string, PropertyDetailsData>) => {
    setCollectedPropertyDetails(data);
    setShowPropertyDetailsModal(false);
    setShowEmailModal(true);
  };

  // Step 2: Handle email submission from modal
  const handleEmailSubmit = async (email: string) => {
    setShowEmailModal(false);

    // Merge collected details with properties
    const propertiesWithAllDetails = propertiesToCollectDetails.map((prop) => ({
      ...prop,
      details: collectedPropertyDetails[prop.propertyId],
    }));

    await submitPropertiesToAPI(propertiesWithAllDetails, email);
  };

  // Handle property details modal close/cancel
  const handlePropertyDetailsClose = () => {
    setShowPropertyDetailsModal(false);
    setPropertiesToCollectDetails([]);
    setCollectedPropertyDetails({});
    formikRef.current?.setSubmitting(false);
  };

  // Handle email modal close/cancel - keep collected details for re-use
  const handleEmailModalClose = () => {
    setShowEmailModal(false);
    formikRef.current?.setSubmitting(false);
  };

  // Submit properties with details to API
  const submitPropertiesToAPI = async (propertiesWithDetails: PropertyWithDetails[], email: string) => {
    try {
      setSubmitError(null);
      const integrationRequestId = null; // Replace with actual source

      const selectedProperties = propertiesWithDetails.map((prop) => ({
        code: prop.propertyCode,
        ratePlans: prop.ratePlans,
        numberOfRooms: prop.details?.numberOfRooms,
        starRating: prop.details?.starRating,
        chainName: prop.details?.chainName,
      }));

      const response = await submitSelectedProperties({
        integrationRequestId,
        email,
        selectedProperties,
      }).unwrap();

      if (response.success) {
        setShowModal(true);
      } else {
        setSubmitError(response.message || 'Failed to submit selection');
      }
    } catch (error: any) {
      console.error('Error submitting selection:', error);
      setSubmitError(
        error.data?.message ||
          'An error occurred while submitting your selection'
      );
    } finally {
      formikRef.current?.setSubmitting(false);
      setPropertiesToCollectDetails([]);
      setCollectedPropertyDetails({});
    }
  };

  // Prepare properties for details collection
  const preparePropertiesForDetailsCollection = (
    selections: Record<string, string[]>
  ): PropertyWithDetails[] => {
    return Object.entries(selections)
      .map(([propertyId, ratePlanIds]) => {
        const property = propertiesWithRatePlans.find((p) => p.id === propertyId);
        const ratePlanIdArray = Array.isArray(ratePlanIds) ? ratePlanIds : [];

        const ratePlans = (property?.ratePlans || [])
          .filter((rp) => ratePlanIdArray.includes(rp.id))
          .map((rp) => ({
            id: rp.id,
            code: rp.code,
            name: rp.name,
            description: rp.description || '',
          }));

        return {
          propertyId,
          propertyCode: property?.code || propertyId,
          propertyName: property?.name || propertyId,
          ratePlans,
        };
      })
      .filter((prop) => prop.ratePlans.length > 0);
  };

  // Convert PropertyWithDetails to PropertyForDetails for the modal
  const getPropertiesForModal = (): PropertyForDetails[] => {
    return propertiesToCollectDetails.map((prop) => ({
      propertyId: prop.propertyId,
      propertyName: prop.propertyName,
    }));
  };

  if (isLoadingProperties) return <Loader />;
  if (isPropertiesError)
    return <div>Error loading properties. Please try again later.</div>;

  return (
    <div className="property-selection">
      <img className="bg-left" src={LeftBg} alt="" />
      <img className="bg-right" src={RightBg} alt="" />

      <header className="header">
        <div className="header__inner">
          <div className="header__brand">
            <img src={HTLogo} alt="Hotel Trader" />
          </div>
        </div>
      </header>

      <div className="property-selection__main">
        <div className="property-selection__main__inner">
          <h2 className="title">Hi, there!</h2>
          <p className="subtitle">
            We noticed you have multiple properties. Please select the
            properties and rate plans you'd like to set up.
          </p>

          <Formik
            innerRef={formikRef}
            initialValues={{ selections: {} }}
            onSubmit={(values) => {
              setSubmitError(null);

              // Prepare properties for details collection
              const propertiesToCollect = preparePropertiesForDetailsCollection(values.selections);

              // Validate that at least one property with rate plans is selected
              if (propertiesToCollect.length === 0) {
                setSubmitError(
                  'Please select at least one rate plan for the selected properties'
                );
                formikRef.current?.setSubmitting(false);
                return;
              }

              // Start property details collection flow (keep existing details for auto-populate)
              setPropertiesToCollectDetails(propertiesToCollect);
              setShowPropertyDetailsModal(true);
            }}
          >
            {({ values, setFieldValue, isSubmitting: isFormSubmitting }) => (
              <Form>
                <PropertyCheckList
                  data={{ properties: propertiesWithRatePlans }}
                  value={values.selections}
                  onChange={(next) => {
                    setFieldValue('selections', next);
                    handlePropertySelection(next);
                  }}
                  showRatePlans={true}
                  onPropertyExpand={handlePropertyExpand}
                />
                <div className="footer">
                  <div className="confirm-bar">
                    <button
                      type="submit"
                      className="confirm-btn"
                      disabled={
                        isFormSubmitting || !isValidSelection(values.selections)
                      }
                    >
                      {isFormSubmitting ? 'Submitting...' : 'Confirm selection'}
                    </button>
                  </div>
                </div>
                {submitError && (
                  <div className="error-message">{submitError}</div>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Success Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        closeButton={false}
        size="small"
        body={
          <div className="success-modal">
            <img src={CheckTick} alt="Success" className="success-icon" />
            <h3>Success!</h3>
            <p>
              Everything is ready to go! Your properties and rate plans are now
              set up, ready for you to manage.
            </p>
            <hr />
            <button className="confirm-btn" onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        }
      />

      {/* Step 1: Property Details Modal (all properties with vertical scroll) */}
      {propertiesToCollectDetails.length > 0 && (
        <PropertyDetailsModal
          open={showPropertyDetailsModal}
          properties={getPropertiesForModal()}
          initialData={collectedPropertyDetails}
          onSubmit={handlePropertyDetailsSubmit}
          onClose={handlePropertyDetailsClose}
        />
      )}

      {/* Step 2: Email Authorization Modal (once at the end) */}
      <AuthorizeEmailModal
        open={showEmailModal}
        onSubmit={handleEmailSubmit}
        onClose={handleEmailModalClose}
      />
    </div>
  );
};

export default PropertySelection;
